import { z } from 'zod';
import {
  ACTIVE_RUN_STATUSES,
  FAILED_RUN_STATUSES,
  isoDateTime,
  jsonObject,
  jsonValue,
  runStatus,
  type RunStatus,
  uuid,
} from './primitives';
import { toolStepStatus } from './steps';
import { artifactBody, artifactSource } from './episode';

/**
 * Run + transcript + usage wire contracts (Master Spec §6.4, §6.5, §14). Shared
 * between the Operations API (WP-10 additions to apps/server) and the web app: the
 * run list, the run-detail header, the step-by-step transcript (model turns, tool
 * calls with args/results, approvals, wakes), and cost/usage per run.
 *
 * The transcript is a projection of `agent_run_steps` (§7.2). The gateway writes
 * `tool_call` rows (with `tool`/`toolStatus`); the episode runner writes
 * `model_turn`/`approval`/`wake`/`system` rows. `payload` is opaque JSONB the web
 * renders per-kind; no work package invents a parallel step shape (§8.2).
 */

/** Rolling budget/usage counters kept on `agent_runs.totals` (§6.4). */
export const runTotals = z
  .object({
    turns: z.number().nonnegative().optional(),
    toolCalls: z.number().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
  })
  .catchall(jsonValue);
export type RunTotals = z.infer<typeof runTotals>;

/**
 * Runs-list query flag (§14, v1.11.0, #215). By DEFAULT the runs list HIDES runs whose
 * agent is `archived` (an archived agent is hidden from the ops views). Set
 * `includeArchived: true` to include them (the "Include archived agents' runs" toggle).
 * Cost analytics are unaffected — an archived agent's cost stays in the daily totals.
 * A query param arrives as the string 'true'/'false' (or already a boolean from a test),
 * so accept both and normalize to a boolean.
 */
export const includeArchivedRunsParam = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform((v) => v === true || v === 'true')
  .optional();

/**
 * Derived run OUTCOME (§14, v1.12.0, #263). There is no single stored column — the
 * runs service derives this per run from its status + result signals:
 *   - `result`     — a terminal-successful run that produced a tangible output: it has
 *                    ≥1 `artifact`, ≥1 `approval` (a proposed/executed risky action), or
 *                    ≥1 SUCCESSFUL MUTATING `tool_call` step (an inbox DRAFT is this
 *                    signal — `email.create_draft` — NOT an artifact). The mutating-tool
 *                    set is the gateway's write classification (`sideEffect !== 'none'`).
 *   - `no_result`  — a terminal-successful run (`COMPLETED`/`LIMIT_REACHED`) with NONE of
 *                    those signals: the "no action needed"/filtered/marketing path.
 *   - `failed`     — `FAILED`/`CANCELLED`/`LIMIT_EXCEEDED`/`BLOCKED`.
 *   - `active`     — `QUEUED`/`RUNNING`/`WAITING`/`WAITING_FOR_APPROVAL` (still going; a
 *                    live run is NEVER hidden by the default filter).
 */
export const runOutcome = z.enum(['result', 'no_result', 'failed', 'active']);
export type RunOutcome = z.infer<typeof runOutcome>;

/**
 * The KIND of tangible output a `result` run produced (§14, #263) — drives the outcome
 * badge label. `null` for every non-`result` outcome. `action` is the generic fallback
 * for a mutating tool call / approval that doesn't map to a more specific kind.
 */
export const runResultKind = z.enum(['sent', 'draft', 'event', 'file', 'report', 'action']);
export type RunResultKind = z.infer<typeof runResultKind>;

/** The short human label the outcome badge shows for each `resultKind`. */
export const RUN_RESULT_KIND_LABELS: Record<RunResultKind, string> = {
  sent: 'Sent',
  draft: 'Draft',
  event: 'Event',
  file: 'File',
  report: 'Report',
  action: 'Action',
};

/** Pure label lookup for a `resultKind` (server derives it; web can also render it). */
export function runResultLabel(kind: RunResultKind): string {
  return RUN_RESULT_KIND_LABELS[kind];
}

/**
 * Runs-list `outcome` FILTER (§14, #263). Selects which outcome bucket the list shows:
 *   - `results` (DEFAULT) — result-producing runs PLUS currently-active runs (a live run
 *     is never hidden). Hides `no_result` and `failed`.
 *   - `no_action` — only `no_result` runs.
 *   - `failed` — only `failed` runs.
 *   - `all` — everything (no outcome filter).
 * Arrives as a query string; unknown/absent values fall back to the caller's default
 * (`results` at the HTTP boundary, so the API is result-first without a client change).
 */
export const runsListOutcomeFilter = z.enum(['results', 'no_action', 'failed', 'all']);
export type RunsListOutcomeFilter = z.infer<typeof runsListOutcomeFilter>;

/**
 * Pure outcome derivation (§14, #263). Maps a run status + whether it carries ANY result
 * signal (artifact / approval / successful mutating tool call) to its outcome bucket. The
 * status partitions (`ACTIVE_/FAILED_/SUCCESS_RUN_STATUSES`) are the single source of
 * truth shared with the list SQL. Used by the run-detail / cancel paths (which know the
 * status and can cheaply probe signals); the LIST derives the same result in SQL.
 */
export function deriveRunOutcome(status: RunStatus, hasResultSignal: boolean): RunOutcome {
  if ((ACTIVE_RUN_STATUSES as readonly string[]).includes(status)) return 'active';
  if ((FAILED_RUN_STATUSES as readonly string[]).includes(status)) return 'failed';
  // SUCCESS_RUN_STATUSES: result-producing only if a signal is present.
  return hasResultSignal ? 'result' : 'no_result';
}

/** A row in the run list (§14 operations). Minimal header for the list view. */
export const runSummary = z.object({
  id: uuid,
  agentId: uuid.nullable(),
  status: runStatus,
  /**
   * Optional short operator label for this run (§13.2, #147). Set for a `templated`
   * run to name it in the run list; null for every existing run and for `static` runs
   * with no label. Surfaced in the list header.
   */
  label: z.string().nullable(),
  /**
   * Thread CORRELATION id (§14, #299). For a run triggered by an inbound email, the
   * `threadId` of its trigger event — a first-class stored column (`agent_runs.thread_id`)
   * read plainly (no per-row events join). `null` for manual/scheduled runs (no thread)
   * and for every legacy run created before the backfill had a trigger event to read.
   * Backs the runs-list `threadId` filter and the run-detail "runs on this thread" link.
   */
  threadId: z.string().nullable(),
  /**
   * Newer-message FLAG (§12/§7, #300). True when a NEWER inbound email arrived on this run's
   * thread while it was PARKED (a draft awaiting approval, or BLOCKED), or when it parked
   * WAITING_FOR_APPROVAL with a newer sibling already on the thread. Warns the operator the
   * run's draft may be outdated ("a newer message has arrived on this thread"). Replaces #78
   * supersede-cancel: the run is FLAGGED, never cancelled. Defaults false; additive &
   * back-compat (an older payload without it parses as false).
   */
  newerThreadMessage: z.boolean().default(false),
  progressSummary: z.string().nullable(),
  endedReason: z.string().nullable(),
  /**
   * When a run is parked WAITING on a timer (`task.wait` / `task.schedule_follow_up`,
   * §7.3), the instant it is scheduled to resume. Null unless WAITING-on-timer — the
   * run view surfaces it as "Waiting until …" so a timed pause is visible.
   */
  resumeAt: isoDateTime.nullable(),
  totals: runTotals,
  startedAt: isoDateTime.nullable(),
  endedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  /**
   * DERIVED run outcome (§14, #263) — not a stored column. Computed by the runs service
   * from the run's status + result signals (artifacts / approvals / successful mutating
   * tool-call steps). Backs the outcome badge and the runs-list `outcome` filter.
   */
  outcome: runOutcome,
  /**
   * For a `result` run, the KIND of output produced (drives the badge label, e.g. "Draft"
   * / "Sent" / "Report"). `null` for every non-`result` outcome. Additive & back-compat:
   * an older payload without these fields still parses everywhere they are optional.
   */
  resultKind: runResultKind.nullable(),
  /** The badge label for `resultKind` (server-derived convenience). `null` when no result. */
  resultLabel: z.string().nullable(),
});
export type RunSummary = z.infer<typeof runSummary>;

/** Max length of a derived run label — long enough to be distinguishing, short
 * enough for a single-line list tag (§13.2, #147/#155). */
export const RUN_LABEL_MAX_LENGTH = 60;

/**
 * Derive a short operator run label from a per-run task prompt (§13.2, #155). Pure:
 * takes the FIRST non-empty line, trims it, collapses internal whitespace runs to a
 * single space, and truncates to ~60 chars (with an ellipsis). An empty or
 * whitespace-only prompt yields an empty string — the caller treats that as "no
 * label" and leaves the `label` column NULL. Used server-side when a `templated`
 * agent is fired, so the run list can name repeat runs of the same agent.
 */
export function deriveRunLabel(taskPrompt: string): string {
  const firstNonEmptyLine =
    taskPrompt.split('\n').find((line) => line.trim().length > 0) ?? '';
  const collapsed = firstNonEmptyLine.trim().replace(/\s+/g, ' ');
  if (collapsed.length <= RUN_LABEL_MAX_LENGTH) return collapsed;
  return `${collapsed.slice(0, RUN_LABEL_MAX_LENGTH - 1).trimEnd()}…`;
}

/** One transcript step as surfaced to the web (§7.2 `agent_run_steps`). */
export const runStep = z.object({
  stepNo: z.number().int().positive(),
  kind: z.enum(['model_turn', 'tool_call', 'approval', 'wake', 'system']),
  payload: jsonObject,
  tool: z.string().nullable(),
  toolStatus: toolStepStatus.nullable(),
  cost: jsonObject.nullable(),
  createdAt: isoDateTime,
});
export type RunStep = z.infer<typeof runStep>;

/** A usage-ledger row for a run (§6.5). */
export const runUsageRecord = z.object({
  id: uuid,
  kind: z.string(),
  quantity: jsonObject,
  costUsd: z.number().nonnegative(),
  at: isoDateTime,
});
export type RunUsageRecord = z.infer<typeof runUsageRecord>;

/**
 * An artifact surfaced on the run detail (§6.5 `artifacts`). Final outputs that
 * cite the web carry `sources` (hosted-research citations), rendered in the run
 * view (§13). `body` is the one concrete shape; `sources` is null when the output
 * did not cite the web.
 */
export const runArtifact = z.object({
  id: uuid,
  kind: z.enum(['draft_email', 'report', 'file', 'structured']),
  title: z.string(),
  body: artifactBody,
  sources: z.array(artifactSource).nullable(),
  createdAt: isoDateTime,
});
export type RunArtifact = z.infer<typeof runArtifact>;

/** Aggregated cost/usage per run (§14 "cost and usage per run"). */
export const runUsageSummary = z.object({
  totalCostUsd: z.number().nonnegative(),
  totalTokensIn: z.number().nonnegative(),
  totalTokensOut: z.number().nonnegative(),
  records: z.array(runUsageRecord),
});
export type RunUsageSummary = z.infer<typeof runUsageSummary>;

/**
 * The run-detail response (§14): the run header, the FIRST PAGE of the transcript,
 * and the usage rollup. One fetch backs the run page; the SSE stream then appends new
 * steps by `stepNo` (monotonic id) without a refetch.
 *
 * The transcript is keyset-paginated on `stepNo` (a run can accrue unboundedly many
 * steps, each JSONB up to 512 KB — materialising them all is a memory/latency
 * hazard). `nextStepCursor` is the `stepNo` to pass as `after` to fetch the next
 * page; `null` means the transcript is fully loaded.
 */
export const runDetail = z.object({
  run: runSummary,
  /**
   * The per-run core task prompt (§13.2, #147). Set for a `templated` run to the text
   * the operator edited for THIS run; stored on the run (`agent_runs.task_prompt`),
   * NOT a new version (I10 — a run still references one immutable version). Null for a
   * `static` run and for every existing run (back-compat). `label` rides on `run`
   * (the `runSummary` above).
   */
  taskPrompt: z.string().nullable(),
  /**
   * The run's agent display name (§14, #193). `agent_runs.agent_id` is a forward
   * reference (no FK, §6.4) — resolved via a separate tenant-scoped read. Null when
   * the run has no agent link, or the agent was since deleted.
   */
  agentName: z.string().nullable(),
  /**
   * The run's EFFECTIVE core prompt, read from its PINNED `agent_versions.config`
   * (§14, #193, I10 — the version is immutable; a `templated` run's per-run override
   * rides separately on `taskPrompt` above). Kind-dependent: `research.prompt` for a
   * `kind:'research'` version, else `instructions` — mirrors the runner's own
   * resolution (runtime/runner.ts). Null when the run has no pinned version, the
   * version was since deleted, or its config fails to parse.
   */
  agentPrompt: z.string().nullable(),
  steps: z.array(runStep),
  /** Keyset cursor: the highest `stepNo` returned, or null when no more steps remain. */
  nextStepCursor: z.number().int().positive().nullable(),
  usage: runUsageSummary,
  /** Artifacts the run produced (§6.5); web-citing outputs carry `sources` (§13). */
  artifacts: z.array(runArtifact),
});
export type RunDetail = z.infer<typeof runDetail>;

/**
 * A keyset page of transcript steps (`GET …/runs/:runId/steps?after=&limit=`). Used to
 * lazily load the rest of a long transcript after the initial `runDetail` page.
 */
export const runStepsPage = z.object({
  steps: z.array(runStep),
  nextStepCursor: z.number().int().positive().nullable(),
});
export type RunStepsPage = z.infer<typeof runStepsPage>;
